const LOCK_ERROR_CODES = [
  'no-parameters-found-for-template',
  'template-not-found',
  'parameter-weights-sum-not-100',
  'sub-parameter-weights-sum-not-100',
] as const;

type LockErrorCode = (typeof LOCK_ERROR_CODES)[number];

const WEIGHT_RELATED_CODES = new Set<string>([
  'no-parameters-found-for-template',
  'parameter-weights-sum-not-100',
  'sub-parameter-weights-sum-not-100',
]);

const LOCK_ERROR_LABEL_PREFIX = 'lock-error-';

function parsePipePayload(raw: string): Record<string, string> {
  const payload: Record<string, string> = {};
  const segments = raw.split('|').slice(1);

  segments.forEach((segment) => {
    const separatorIndex = segment.indexOf('=');
    if (separatorIndex === -1) {
      return;
    }

    const key = segment.slice(0, separatorIndex);
    const value = segment.slice(separatorIndex + 1);
    if (key) {
      payload[key] = value;
    }
  });

  return payload;
}

function applyTemplate(template: string, values: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => values[key] ?? '');
}

function isKnownLockErrorCode(code: string): code is LockErrorCode {
  return LOCK_ERROR_CODES.includes(code as LockErrorCode);
}

function getLockErrorCode(raw: string): LockErrorCode | null {
  const baseCode = raw.split('|')[0];
  return isKnownLockErrorCode(baseCode) ? baseCode : null;
}

export function toLockErrorLabelKey(code: LockErrorCode): string {
  return `${LOCK_ERROR_LABEL_PREFIX}${code}`;
}

export function isWeightRelatedLockError(raw: string): boolean {
  const baseCode = raw.split('|')[0];
  return WEIGHT_RELATED_CODES.has(baseCode);
}

export function mapLockErrorCode(raw: string, t: (key: string) => string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return raw;
  }

  const code = getLockErrorCode(trimmed);
  if (!code) {
    return trimmed;
  }

  const payload = parsePipePayload(trimmed);
  return applyTemplate(t(toLockErrorLabelKey(code)), payload);
}

export function mapLockErrorMessage(raw: string, t: (key: string) => string): string {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return raw;
  }

  return lines.map((line) => mapLockErrorCode(line, t)).join('\n');
}

function hasMappedLockError(raw: string): boolean {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .some((line) => getLockErrorCode(line) !== null);
}

export function mapRequestLockError(
  error: unknown,
  t: (key: string) => string,
  fallback: string,
): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const axiosError = error as {
    response?: { data?: unknown };
    message?: string;
  };

  let rawMessage: string | undefined;

  if (typeof axiosError.response?.data === 'string') {
    rawMessage = axiosError.response.data;
  } else if (
    axiosError.response?.data &&
    typeof axiosError.response.data === 'object' &&
    'message' in axiosError.response.data &&
    typeof (axiosError.response.data as { message?: unknown }).message === 'string'
  ) {
    rawMessage = (axiosError.response.data as { message: string }).message;
  } else if (typeof axiosError.message === 'string') {
    rawMessage = axiosError.message;
  }

  if (!rawMessage?.trim()) {
    return fallback;
  }

  if (hasMappedLockError(rawMessage)) {
    return mapLockErrorMessage(rawMessage, t);
  }

  return rawMessage.trim();
}
